import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import { getAppAuthById } from 'API/AppAuth';
import config from '@/config.json';

// import VisibleScopeField from './formFields/VisibleScopeField';
import ZoomManagedByField from './formFields/ZoomManagedByField';
import ZoomMaxAccessFailuresField from './formFields/ZoomMaxAccessFailuresField';
import ZoomAccessFailureWindowField from './formFields/ZoomAccessFailureWindowField';
import ZoomUserSyncField from './formFields/ZoomUserSyncField';

const WebexAuthServiceEdit = () => {
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [error] = useState(null);

    const { currentCompany: companyId } = useSelector((state) => ({
        ...state.navigationLists,
    }));

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope: accountScope } = useSelector((state) => ({
        ...state.login,
    }));

    const managedByOptions = config.users.managedBy;

    const {
        data: appAuthData,
        status,
        isFetching,
    } = useQuery({
        queryKey: ['WebexAuthService', { id }],
        queryFn: async () => {
            const res = await getAppAuthById(id);
            return res.data;
        },
        enabled: !!id,
        refetchOnWindowFocus: false,
    });

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    if (status === 'loading' || isFetching) {
        return <LoadingIndicator />;
    }

    if (appAuthData?.companyID !== companyId) {
        return <Navigate to="/dashboard-services" />;
    }

    // Map data for form initialization
    const initialValues = {
        name: state?.service?.serviceType || 'Webex',
        defaultManagedBy: appAuthData?.defaultManagedBy || 0,
        maxAccessFailures: appAuthData?.maxAccessFailures || 0,
        accessFailureWindow: appAuthData?.accessFailureWindow || 0,
        userSync: appAuthData?.userSync || false,
        visibleScope: 0, // Default scope
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: 'View Webex Service', path: '' },
    ];

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`Viewing ${state?.service?.serviceType || 'Webex'}`}
            initialValues={initialValues}
            finalFormSubmit={() => {}} // Read-only, no submit
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            noBackBtn={false}>
            {
                ({ values, setFieldValue, errors, touched }) =>
                    [
                        <ZoomManagedByField
                            key="defaultManagedBy"
                            field={{
                                name: 'defaultManagedBy',
                                value: values.defaultManagedBy,
                            }}
                            form={{ values, setFieldValue, errors, touched }}
                            options={managedByOptions}
                            darkMode={darkMode}
                            disabled={true}
                        />,
                        accountScope > 20 && (
                            <ZoomMaxAccessFailuresField
                                key="maxAccessFailures"
                                field={{
                                    name: 'maxAccessFailures',
                                    value: values.maxAccessFailures,
                                }}
                                form={{
                                    values,
                                    setFieldValue,
                                    errors,
                                    touched,
                                }}
                                darkMode={darkMode}
                                disabled={true}
                            />
                        ),
                        accountScope > 20 && (
                            <ZoomAccessFailureWindowField
                                key="accessFailureWindow"
                                field={{
                                    name: 'accessFailureWindow',
                                    value: values.accessFailureWindow,
                                }}
                                form={{
                                    values,
                                    setFieldValue,
                                    errors,
                                    touched,
                                }}
                                darkMode={darkMode}
                                disabled={true}
                            />
                        ),
                        <ZoomUserSyncField
                            key="userSync"
                            field={{ name: 'userSync', value: values.userSync }}
                            form={{ values, setFieldValue, errors, touched }}
                            darkMode={darkMode}
                            disabled={true}
                        />,
                        // <VisibleScopeField
                        //     key="visibleScope"
                        //     field={{
                        //         name: 'visibleScope',
                        //         value: values.visibleScope,
                        //     }}
                        //     form={{ values, setFieldValue, errors, touched }}
                        //     options={[{ value: 0, label: 'Global' }]}
                        //     darkMode={darkMode}
                        //     disabled={true}
                        // />,
                        error && (
                            <Alert
                                key="error"
                                status="error"
                                sx={{ marginTop: '1rem' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ),
                    ].filter(Boolean) // Filter out false values from conditional rendering
            }
        </FormBase>
    );
};

export default WebexAuthServiceEdit;
